import type { Ref } from "react";
import { styled } from "@pigment-css/react";
import { useLocale } from "@acto/web-common/locales";
import { ChevronRightThin } from "@acto/web-common/components";
import {
  RowBody,
  RowButton,
  RowChevron,
  RowEyebrow,
  RowItem,
  RowList,
  RowMeta,
  RowTitle,
} from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { formatNumericDayMonth } from "@acto/taskpane/field-sales-2/utils/format";

const OrderList = styled(RowList)(({ theme }) => ({
  gap: theme.fieldSalesTwo.spacing[24],
}));

const OrderItem = styled(RowItem)(({ theme }) => ({
  paddingTop: theme.fieldSalesTwo.spacing[24],
}));

const DateBadge = styled("div")(({ theme }) => ({
  flex: "0 0 auto",
  alignSelf: "flex-start",
  width: 56,
  height: 52,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: theme.fieldSalesTwo.radius[16],
  background: "rgba(19,19,19,0.04)",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[13],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: 1.32,
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.wide,
  color: theme.fieldSalesTwo.colors.neutral[800],
  flexShrink: 0,
}));

const OrderBody = styled(RowBody)({ gap: 0 });

const OrderStatus = styled(RowEyebrow)({ minHeight: 28 });

const OrderNumber = styled(RowTitle)(({ theme }) => ({
  fontSize: theme.fieldSalesTwo.font.size[16],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
}));

const OrderMeta = styled(RowMeta)(({ theme }) => ({
  fontSize: theme.fieldSalesTwo.font.size[14],
}));

const ScrollSentinel = styled("li")({ height: 1, listStyle: "none" });

export interface OrderRowItem {
  id: string;
  /** ISO date of the order. */
  date: string;
  externalReference: string;
  formattedIncomingOrder: string;
  statusLabel?: string;
}

export interface OrderRowsProps {
  items: OrderRowItem[];
  onOpenOrder: (order: OrderRowItem) => void;
  sentinelRef?: Ref<HTMLLIElement>;
}

export function OrderRows({ items, onOpenOrder, sentinelRef }: OrderRowsProps) {
  const { locale } = useLocale();

  return (
    <OrderList role="list">
      {items.map((order) => (
        <OrderItem key={order.id}>
          <RowButton type="button" onClick={() => onOpenOrder(order)}>
            <DateBadge>{formatNumericDayMonth(order.date, locale)}</DateBadge>
            <OrderBody>
              {order.statusLabel && <OrderStatus>{order.statusLabel}</OrderStatus>}
              <OrderNumber>{order.externalReference}</OrderNumber>
              <OrderMeta>{order.formattedIncomingOrder}</OrderMeta>
            </OrderBody>
            <RowChevron>
              <ChevronRightThin strokeWidth={1.5} />
            </RowChevron>
          </RowButton>
        </OrderItem>
      ))}
      {sentinelRef && <ScrollSentinel ref={sentinelRef} aria-hidden="true" />}
    </OrderList>
  );
}
